import { useSceneEvents } from "@/components/common/scene-events-context";
import { planesToObb } from "@/hooks/use-clipping-planes";
import type { GaussianSplatsObject } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { CameraMonitor } from "@faro-lotv/lotv";
import { selectIElementWorldTransform } from "@faro-lotv/project-source";
import {
  SparkRenderer,
  SplatEdit,
  SplatEditRgbaBlendMode,
  SplatEditSdf,
  SplatEditSdfType,
} from "@faro-lotv/spark-gaussian-splats";
import { UPDATE_CAMERA_MONITOR_PRIORITY } from "@faro-lotv/spatial-ui";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef, type JSX } from "react";
import type { Plane, Scene } from "three";

const EPSILON = 1e-6;

type SplatMeshRendererProps = {
  splatMesh: GaussianSplatsObject;

  /** Optional clipping planes, the splats outside the box they delimit are hidden */
  clippingPlanes?: Plane[];
};

/**
 * The sparkjs library requires exactly one SparkRenderer per scene, so it's shared by all the
 * SplatMeshRenderer instances and reference counted to know when it can leave the scene again.
 */
const SPARK_RENDERERS = new WeakMap<
  Scene,
  { renderer: SparkRenderer; refCount: number }
>();

/**
 * The performance mode at which the splats are being rendered
 */
type Performance = "high" | "low";

/**
 * Fetching re-drives itself and a finished sort reports back through `onDirty`, so neither needs a
 * frame while it runs. Moving a decoded page to the GPU only happens inside Spark's `onBeforeRender`.
 *
 * @param renderer The Spark renderer shared by the scene
 * @returns whether Spark needs a frame to hand new splat data to the GPU
 */
function hasPendingWork(renderer: SparkRenderer): boolean {
  const { pager } = renderer;
  if (!pager) return renderer.activeSplats === 0;

  if (
    pager.newUploads.length > 0 ||
    pager.readyUploads.length > 0 ||
    pager.lodTreeUpdates.length > 0
  ) {
    return true;
  }

  // Until the first chunk is requested Spark still needs a frame to run driveLod and start fetching.
  return renderer.activeSplats === 0 && pager.fetchers.length === 0;
}

/**
 * @returns a component to render a gaussian splat dataset
 */
export function SplatMeshRenderer({
  splatMesh,
  clippingPlanes,
}: SplatMeshRendererProps): JSX.Element {
  const transform = useAppSelector(
    selectIElementWorldTransform(splatMesh.iElement.id),
  );

  const scene = useThree((s) => s.scene);
  const gl = useThree((s) => s.gl);

  const sceneEvents = useSceneEvents();
  const invalidate = useCallback(
    () => sceneEvents.invalidateGaussianSplatsScene.emit(),
    [sceneEvents],
  );

  // The splats are rendered on demand, so any change to their pose has to be signalled explicitly.
  useEffect(invalidate, [transform, invalidate]);

  // The scene is shared across modes, so the SparkRenderer must leave it when the last splat
  // unmounts. Otherwise it keeps sorting/rendering splats in modes that don't support them.
  useEffect(() => {
    const existing = SPARK_RENDERERS.get(scene);
    const entry = existing ?? {
      renderer: new SparkRenderer({
        renderer: gl,
        focalAdjustment: 2.0,
        pagedExtSplats: true,
      }),
      refCount: 0,
    };
    SPARK_RENDERERS.set(scene, entry);

    if (entry.refCount === 0) {
      entry.renderer.autoUpdate = true;
      scene.add(entry.renderer);
    }
    entry.refCount += 1;
    // Spark signals asynchronously when a new sort order or LoD page is ready to be drawn.
    entry.renderer.onDirty = invalidate;
    invalidate();

    return () => {
      invalidate();
      entry.refCount -= 1;
      if (entry.refCount > 0) return;

      scene.remove(entry.renderer);
      // Detached but kept alive: the SplatMeshes are cached and reused, so pairing them with a
      // fresh SparkRenderer drains its accumulator pool ("No next accumulator"). autoUpdate and
      // the pending timeout are cleared because dispose()/removal don't cancel a scheduled update.
      entry.renderer.autoUpdate = false;
      entry.renderer.onDirty = undefined;
      clearTimeout(entry.renderer.updateTimeoutId);
      entry.renderer.updateTimeoutId = -1;
    };
  }, [scene, gl, invalidate]);

  // Track if the camera is moving
  const cameraMonitor = useMemo(() => new CameraMonitor(), []);

  // Spark schedules its accumulate/sort/page-fetch work exclusively from `onBeforeRender`, so an
  // on-demand subscene that stopped drawing it would freeze the progressive load half-way.
  const sparkPending = useRef(false);

  const performance = useRef<Performance>("high");
  useFrame(({ camera }, delta) => {
    const { renderer } = SPARK_RENDERERS.get(scene) ?? {};
    if (!renderer) return;
    // If the camera is moving, decrease the dimension of the splats
    // on screen, to reduce resources usage
    let nextPerformance: Performance = performance.current;

    const dpr = window.devicePixelRatio;

    cameraMonitor.checkCameraMovement(camera, delta);
    if (cameraMonitor.cameraMoving) {
      renderer.maxStdDev = Math.sqrt(8 / dpr);
      renderer.lodRenderScale = dpr;
      nextPerformance = "low";
    } else {
      renderer.maxStdDev = Math.sqrt(8);
      renderer.lodRenderScale = 1;
      nextPerformance = "high";
    }

    // The extra frame after the work settles draws the result of the last update.
    const pending = hasPendingWork(renderer);
    if (nextPerformance !== performance.current) {
      performance.current = nextPerformance;
      renderer.setDirty();
      invalidate();
    } else if (pending || sparkPending.current || cameraMonitor.cameraMoving) {
      invalidate();
    }
    sparkPending.current = pending;
  }, UPDATE_CAMERA_MONITOR_PRIORITY);

  // Spark ignores three.js clipping planes. Its documented way to remove splats from a region of
  // space is an SDF edit multiplying their opacity by 0, here inverted to keep only the box inside.
  const clip = useMemo(() => {
    const sdf = new SplatEditSdf({
      type: SplatEditSdfType.BOX,
      invert: true,
      opacity: 0,
    });
    return {
      sdf,
      edit: new SplatEdit({
        rgbaBlendMode: SplatEditRgbaBlendMode.MULTIPLY,
        sdfs: [sdf],
      }),
    };
  }, []);

  // The mesh is cached and reused, so the edit must not outlive this renderer
  useEffect(
    () => () => {
      splatMesh.edits = null;
    },
    [splatMesh],
  );

  // BoxControls mutates its planes array in place, so the box has to be polled to follow the gizmo
  // while the user drags it instead of only when the interaction ends.
  useFrame(() => {
    // The edits are applied after the object-to-world transform, so the SDF lives in world space.
    const box = clippingPlanes ? planesToObb(clippingPlanes) : undefined;
    if (!box) {
      if (splatMesh.edits?.length) {
        splatMesh.edits = [];
        invalidate();
      }
      return;
    }

    const halfSize = box.size.multiplyScalar(0.5);
    if (
      splatMesh.edits?.length &&
      clip.sdf.position.distanceToSquared(box.position) < EPSILON &&
      clip.sdf.quaternion.angleTo(box.quaternion) < EPSILON &&
      clip.sdf.scale.distanceToSquared(halfSize) < EPSILON
    ) {
      return;
    }

    clip.sdf.position.copy(box.position);
    clip.sdf.quaternion.copy(box.quaternion);
    clip.sdf.scale.copy(halfSize);
    clip.sdf.updateMatrixWorld();
    splatMesh.edits = [clip.edit];
    invalidate();
  });

  return (
    <group {...transform}>
      {/* splatMesh is cached/reused (object-cache.ts); dispose={null} avoids R3F disposing it on unmount */}
      <primitive object={splatMesh} dispose={null} />
    </group>
  );
}
